import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, BrainCircuit, Check, Compass, Route, ShieldCheck, Sparkles } from "lucide-react";
import { DISCLAIMER } from "../lib/constants";
import { api } from "../lib/api";

const paths = [
  { title: "Business Analyst", score: 91, tags: ["Commerce", "Communication", "Tech-adjacent"] },
  { title: "Digital Marketing", score: 84, tags: ["Creativity", "Analytics", "Business"] },
  { title: "Product Operations", score: 79, tags: ["Structure", "People", "Problem-solving"] }
];

export function LandingPage() {
  const [reportsGenerated, setReportsGenerated] = useState<number | null>(null);
  useEffect(() => {
    api.publicStats().then((stats) => setReportsGenerated(stats.reportsGenerated)).catch(() => undefined);
  }, []);
  return (
    <>
      <section className="relative overflow-hidden px-5 pb-20 pt-12 md:px-10 md:pb-32 md:pt-20">
        <div className="pointer-events-none absolute -right-24 top-8 h-80 w-80 rounded-full border-[70px] border-lime/30" />
        <div className="shell relative grid items-center gap-14 lg:grid-cols-[1.1fr_.9fr]">
          <div>
            <div className="mb-7 inline-flex items-center gap-2 rounded-full border border-moss/15 bg-white/60 px-4 py-2 text-xs font-extrabold uppercase tracking-[.16em] text-moss backdrop-blur">
              <Sparkles size={15} className="text-coral" /> Five AI agents. One practical plan.
            </div>
            <h1 className="max-w-4xl font-display text-[clamp(4rem,8vw,7.5rem)] leading-[.86] tracking-[-.045em] text-ink">
              Turn potential<br /><em className="text-coral">into a plan.</em>
            </h1>
            <p className="mt-8 max-w-2xl text-lg leading-8 text-ink/65">Discover realistic career paths built around your education, interests, skills, goals, and real-life constraints—then test them with a 30-day action plan.</p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <Link className="btn-dark justify-center px-7 py-4" to="/assessment">Start free assessment <ArrowRight size={18} /></Link>
              <a className="btn-ghost justify-center px-7 py-4" href="#how">See how it works</a>
            </div>
            {reportsGenerated !== null && <div className="mt-6 inline-flex items-center gap-3 rounded-2xl border border-moss/10 bg-white/70 px-4 py-3 shadow-sm"><span className="font-display text-3xl text-moss">{reportsGenerated.toLocaleString("en-IN")}</span><span className="max-w-36 text-xs font-extrabold uppercase leading-4 tracking-[.12em] text-ink/55">career reports generated so far</span></div>}
            <p className="mt-5 text-xs font-semibold text-moss/55">Free preview • About 10 minutes • No rigid “one right career” answer</p>
          </div>
          <div className="relative">
            <div className="rounded-[2.5rem] bg-moss p-5 shadow-soft md:p-8">
              <div className="mb-10 flex items-center justify-between text-white">
                <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-[.18em]"><Compass className="text-lime" /> Sample compass</span>
                <span className="rounded-full bg-white/10 px-3 py-1 text-xs">3 of 5 paths</span>
              </div>
              <div className="space-y-3">
                {paths.map((path, index) => (
                  <div className={`rounded-2xl p-5 ${index === 0 ? "bg-lime text-ink" : "bg-white/8 text-white"}`} key={path.title}>
                    <div className="flex items-center justify-between">
                      <div><span className="text-[10px] font-extrabold uppercase tracking-[.18em] opacity-55">Direction 0{index + 1}</span><h3 className="mt-1 font-display text-2xl">{path.title}</h3></div>
                      <b className="text-xl">{path.score}%</b>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">{path.tags.map((tag) => <span className="rounded-full border border-current/15 px-2.5 py-1 text-[10px] font-bold" key={tag}>{tag}</span>)}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="absolute -bottom-6 -left-5 rounded-2xl bg-coral p-5 shadow-soft md:-left-12"><p className="text-xs font-extrabold uppercase tracking-widest">First move</p><p className="mt-1 font-display text-xl">Build one proof-of-skill project</p></div>
          </div>
        </div>
      </section>

      <section className="bg-white px-5 py-20 md:px-10 md:py-28" id="how">
        <div className="shell">
          <p className="eyebrow">A better career conversation</p>
          <div className="grid gap-8 lg:grid-cols-[.8fr_1.2fr]">
            <h2 className="section-title">From “What should I become?” to “What should I test next?”</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {[
                [BrainCircuit, "01 — Assess", "Tell us what you’ve studied, enjoy, can do, and need to work around."],
                [Compass, "02 — Match", "Gemini agents compare your profile with 30 realistic career directions."],
                [Route, "03 — Roadmap", "See skill gaps, beginner projects, and a week-by-week plan."],
                [ShieldCheck, "04 — Decide", "Use evidence, alternatives, and trusted human guidance for major choices."]
              ].map(([Icon, title, copy]) => {
                const IconComponent = Icon as typeof BrainCircuit;
                return <article className="rounded-[1.8rem] bg-cream p-6" key={String(title)}><IconComponent className="text-coral" /><h3 className="mt-8 font-display text-2xl">{String(title)}</h3><p className="mt-3 text-sm leading-6 text-ink/60">{String(copy)}</p></article>;
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="px-5 py-20 md:px-10 md:py-28">
        <div className="shell grid gap-7 lg:grid-cols-2">
          <div className="rounded-[2.5rem] bg-coral p-8 md:p-12">
            <p className="eyebrow">Free preview</p><h2 className="font-display text-5xl">Start with signal.</h2>
            <ul className="mt-8 space-y-4">{["Career profile summary", "Top 2 paths with fit scores", "Short fit explanations"].map((item) => <li className="flex gap-3" key={item}><Check size={20} /> {item}</li>)}</ul>
            <p className="mt-10 text-3xl font-extrabold">₹0</p>
          </div>
          <div className="rounded-[2.5rem] bg-moss p-8 text-white md:p-12">
            <p className="eyebrow !text-lime">Detailed report</p><h2 className="font-display text-5xl">Move with a plan.</h2>
            <ul className="mt-8 space-y-4 text-white/80">{["All 5 career paths", "Skill-gap analysis", "30-day roadmap", "Portfolio projects", "Resume and opportunity strategy", "Parent/counsellor summary"].map((item) => <li className="flex gap-3" key={item}><Check className="text-lime" size={20} /> {item}</li>)}</ul>
            <div className="mt-10 flex items-end justify-between"><p><span className="text-4xl font-extrabold">₹499</span><span className="text-sm text-white/50"> for up to 3 full reports</span></p><Link className="rounded-full bg-lime px-5 py-3 text-sm font-extrabold text-ink" to="/assessment">Begin <ArrowRight className="inline" size={16} /></Link></div>
          </div>
        </div>
      </section>

      <section className="px-5 pb-20 md:px-10"><div className="shell rounded-3xl border border-moss/10 bg-white p-6 text-sm leading-6 text-ink/55"><ShieldCheck className="mb-3 text-moss" />{DISCLAIMER}</div></section>
    </>
  );
}
